import { useMemo } from "react";
import CallDialog from "@/components/CallDialog";

/**
 * SharedCallDialog
 *
 * Renders the standard CallDialog UI for an alliance member's shared call.
 * Adapts `share` (server-side shared-call snapshot) into the call-shape that
 * CallDialog expects, and synthesizes dispatches from `share.helpers` so the
 * existing "Required / On-Scene / En-Route" UI shows the actual cross-player
 * response state.
 *
 * Dispatching from this dialog routes through `onSendUnit(share, unitId)`,
 * which delegates to the helper-dispatch API in Game.jsx.
 */
export default function SharedCallDialog({
  open,
  onOpenChange,
  share,
  stations,
  hospitals = [],
  apparatusState,
  dispatches,
  currentUserId,
  onSendUnit,
  onHostDispatch,
  onCancelDispatch,
  onQuickFinish,
  onOpenTransport,
  playerGems = 0,
}) {
  // Adapt the share into a call object CallDialog can render directly.
  const adaptedCall = useMemo(() => {
    if (!share) return null;
    const c = share.call || {};
    // Determine mission state + on-scene start in this priority order:
    //   1) Host actively pushed `requester_state` / `requester_on_scene_started_at`
    //      → use those so the helper's UI mirrors what the host is seeing.
    //   2) Otherwise (host offline) fall back to `all_on_scene_at`, which the
    //      server stamps inside `_autoresolve_share_if_due` the moment combined
    //      coverage meets the call's requirements.
    let state = share.requester_state || "pending";
    let onSceneStartedAt = share.requester_on_scene_started_at || null;
    if (!onSceneStartedAt && share.all_on_scene_at) {
      try {
        onSceneStartedAt = new Date(share.all_on_scene_at).getTime();
        state = "on_scene";
      } catch (_e) { /* leave as-is */ }
    }
    return {
      id: `share:${share.id}`, // unique id used only for cache keys inside CallDialog
      type: c.type,
      lat: c.lat,
      lng: c.lng,
      address: c.address || "",
      revealed: true, // shares are always revealed (the requester already has units en route)
      state,
      onSceneStartedAt,
      requiredUnits: c.requiredUnits || {},
      alternates: c.alternates || undefined,
      onSceneDurationSec: c.onSceneDurationSec || 30,
      upgradedFrom: c.upgradedFrom,
      // Patient list (host syncs this every state change). Helpers see the
      // same Transport buttons as the host so they can pick a hospital for
      // their own on-scene Medic. Required so both sides can clear the call.
      patients: Array.isArray(share.patients) ? share.patients : [],
    };
  }, [share]);

  // Synthesize dispatch rows from helpers (cross-player) PLUS any of MY units
  // currently routed to this share PLUS the requester's own units (so the
  // requirements UI shows the FULL combined response, not just helpers).
  const adaptedDispatches = useMemo(() => {
    if (!share) return [];
    const out = [];
    const viewerIsHost = !!share && currentUserId && share.requester_id === currentUserId;

    if (viewerIsHost) {
      // ---- Host view ----
      // The host's CallDialog should show their OWN local dispatches (full
      // cancel control, immediate state) rather than the 3 s-delayed
      // `requester_units` echo from the server. We also still show alliance
      // helpers (external, can't cancel).
      (dispatches || []).forEach((d) => {
        if (d.callId !== share.call?.id) return;
        out.push({ ...d, callId: adaptedCall.id });
      });
      (share.helpers || []).forEach((h, idx) => {
        out.push({
          id: `share-helper-${share.id}-${idx}`,
          callId: adaptedCall.id,
          apparatusId: `share-${share.id}-${idx}`,
          apparatusName: `${h.apparatusName} (${h.name})`,
          apparatusType: h.apparatusType,
          stationId: null,
          startedAt: h.startedAt || Date.now() - 1000,
          durationSec: h.durationSec || 30,
          onScene: !!h.on_scene,
          returning: !!h.returning,
          helper: true,
          external: true,
        });
      });
      return out;
    }

    // ---- Helper view ----
    // dedup: keys for MY local dispatches to this share — used to skip the
    // backend's echo of those same dispatches so units don't appear twice.
    const myDispatchIds = new Set();
    (dispatches || []).forEach((d) => {
      if (d.sharedId === share.id) myDispatchIds.add(d.id);
    });
    // 1) Requester's own units (host of the call). External — can't cancel.
    (share.requester_units || []).forEach((u, idx) => {
      out.push({
        id: `share-host-${share.id}-${idx}`,
        callId: adaptedCall.id,
        apparatusId: `share-host-${share.id}-${idx}`,
        apparatusName: `${u.apparatusName} (${share.requester_name || "Host"})`,
        apparatusType: u.apparatusType || "Engine",
        stationId: null,
        startedAt: u.startedAt || Date.now() - 1000,
        durationSec: u.durationSec || 30,
        onScene: !!u.on_scene,
        returning: !!u.returning,
        helper: false,
        external: true,
      });
    });
    // 2) Other alliance helpers (skip any rows we'll add from MY local list).
    (share.helpers || []).forEach((h, idx) => {
      const did = h.dispatch_id ?? h.dispatchId; // backend uses snake_case
      if (did != null && myDispatchIds.has(did)) return; // skip my own (we add it below)
      out.push({
        id: `share-helper-${share.id}-${idx}`,
        callId: adaptedCall.id,
        apparatusId: `share-${share.id}-${idx}`,
        apparatusName: `${h.apparatusName} (${h.name})`,
        apparatusType: h.apparatusType,
        stationId: null,
        startedAt: h.startedAt || Date.now() - 1000,
        durationSec: h.durationSec || 30,
        onScene: !!h.on_scene,
        returning: !!h.returning,
        helper: true,
        external: true, // can't cancel another player's truck
      });
    });
    // 3) MY local helper-dispatches for this share — full cancel control.
    (dispatches || []).forEach((d) => {
      if (d.sharedId !== share.id) return;
      out.push({
        ...d,
        callId: adaptedCall.id, // CallDialog filters by callId
      });
    });
    return out;
  }, [share, dispatches, adaptedCall, currentUserId]);

  // If the viewer IS the host of this share, dispatching from this dialog
  // should target their LOCAL call (same code path as the normal CallDialog),
  // not the helper / mutual-aid endpoint. Otherwise the host would push
  // alliance-helper rows for their own units, which double-counts and
  // can't be cancelled like local dispatches. Helpers route via onSendUnit
  // as before.
  const isHost = !!share && currentUserId && share.requester_id === currentUserId;
  const handleDispatch = (_callId, unitId) => {
    if (isHost && onHostDispatch && share?.call?.id != null) {
      onHostDispatch(share.call.id, unitId);
      return;
    }
    if (onSendUnit) onSendUnit(share, unitId);
  };

  const handleCancel = (dispatchId) => {
    // Only let the user cancel their OWN units, not other players'.
    if (onCancelDispatch) onCancelDispatch(dispatchId);
  };

  // Only the host can quick-finish their own call — helpers don't pay or
  // own the call resolution flow. We wrap onQuickFinish so it targets the
  // host's LOCAL call id, not the synthetic "share:<id>" id we created.
  const handleQuickFinish = isHost && onQuickFinish && share?.call?.id != null
    ? () => onQuickFinish(share.call.id)
    : null;

  if (!share || !adaptedCall) return null;

  return (
    <CallDialog
      open={open}
      onOpenChange={onOpenChange}
      call={adaptedCall}
      stations={stations}
      hospitals={hospitals}
      apparatusState={apparatusState}
      dispatches={adaptedDispatches}
      onDispatch={handleDispatch}
      onCancelDispatch={handleCancel}
      onQuickFinish={handleQuickFinish}
      onOpenTransport={onOpenTransport ? (p) => onOpenTransport(p, share) : undefined}
      playerGems={playerGems}
      hasAlliance
      isShared
    />
  );
}
